import React, { useState, useEffect } from 'react';
import { X, BookOpen, ExternalLink, ChevronRight } from 'lucide-react';

/**
 * HelpDrawer - Sliding help panel with context-sensitive content
 *
 * Usage:
 * <HelpDrawer
 *   isOpen={helpOpen}
 *   onClose={() => setHelpOpen(false)}
 *   pageId="dashboard"
 *   viewMode="end_user" // Optional: filters content based on user view
 * />
 */
const HelpDrawer = ({ isOpen, onClose, pageId, customContent, viewMode }) => {
  const [activeSection, setActiveSection] = useState(null);

  useEffect(() => {
    // Close drawer on escape key
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // Get help content for current page
  const getHelpContent = () => {
    if (customContent) return customContent;

    // Import help content configuration
    const helpContent = require('./helpContent').default;
    const baseContent = helpContent[pageId] || helpContent.default;

    // Filter related docs based on view mode
    if (baseContent.relatedDocs && viewMode) {
      const filteredDocs = baseContent.relatedDocs.filter(doc => {
        // End users only see user help
        if (viewMode === 'end_user') {
          return doc.url.includes('user-help') || doc.url.includes('getting-started');
        }
        // Company admins see admin guide and user help, but not technical docs
        if (viewMode === 'company_admin') {
          return !doc.url.includes('technical');
        }
        // Platform admins see everything
        return true;
      });

      return {
        ...baseContent,
        relatedDocs: filteredDocs
      };
    }

    return baseContent;
  };

  const content = getHelpContent();

  if (!isOpen) return null;

  return (
    <>
      {/* Overlay */}
      <div 
        className="fixed inset-0 bg-black/50 z-40 transition-opacity"
        onClick={onClose}
      />

      {/* Drawer */}
      <div className="fixed right-0 top-0 bottom-0 w-full sm:w-96 bg-white shadow-2xl z-50 transform transition-transform duration-300 ease-in-out overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-6 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <BookOpen className="w-6 h-6" />
            <div>
              <h2 className="text-xl font-bold">{content.title}</h2>
              <p className="text-blue-100 text-sm">{content.subtitle}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:bg-white/20 rounded-lg p-2 transition-colors"
            aria-label="Close help"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Quick Overview */}
          {content.overview && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h3 className="font-semibold text-blue-900 mb-2">Quick Overview</h3>
              <p className="text-sm text-blue-800">{content.overview}</p>
            </div>
          )}

          {/* Sections */}
          {content.sections && content.sections.map((section, index) => (
            <div key={index} className="border-b border-gray-200 pb-4 last:border-0">
              <button
                onClick={() => setActiveSection(activeSection === index ? null : index)}
                className="w-full flex items-center justify-between text-left group"
              >
                <h3 className="font-semibold text-gray-900 group-hover:text-blue-600 transition-colors">
                  {section.title}
                </h3>
                <ChevronRight 
                  className={`w-5 h-5 text-gray-400 transition-transform ${
                    activeSection === index ? 'rotate-90' : ''
                  }`}
                />
              </button>
              
              {activeSection === index && (
                <div className="mt-3 space-y-3 animate-in slide-in-from-top-2 duration-200">
                  <p className="text-sm text-gray-600">{section.content}</p>
                  
                  {section.steps && (
                    <ol className="list-decimal list-inside space-y-2 text-sm text-gray-700">
                      {section.steps.map((step, i) => (
                        <li key={i} className="pl-2">{step}</li>
                      ))}
                    </ol>
                  )}

                  {section.tips && (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                      <p className="text-xs font-semibold text-yellow-900 mb-1">💡 Tips</p>
                      <ul className="text-sm text-yellow-800 space-y-1">
                        {section.tips.map((tip, i) => (
                          <li key={i}>• {tip}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {section.example && (
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                      <p className="text-xs font-semibold text-gray-700 mb-2">Example:</p>
                      <pre className="text-xs text-gray-600 whitespace-pre-wrap font-mono">
                        {section.example}
                      </pre>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}

          {/* Related Documentation */}
          {content.relatedDocs && content.relatedDocs.length > 0 && (
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="font-semibold text-gray-900 mb-3">📚 Related Documentation</h3>
              <div className="space-y-2">
                {content.relatedDocs.map((doc, index) => (
                  <a
                    key={index}
                    href={doc.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-between text-sm text-blue-600 hover:text-blue-800 hover:bg-blue-50 p-2 rounded transition-colors"
                  >
                    <span>{doc.title}</span>
                    <ExternalLink className="w-4 h-4" />
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 p-4 bg-gray-50">
          <p className="text-xs text-gray-600 text-center">
            Need more help? <a href="mailto:support@acquiva.com" className="text-blue-600 hover:underline">Contact Support</a>
          </p>
        </div>
      </div>
    </>
  );
};

export default HelpDrawer;

